import classNames from "classnames";
import React, {
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  LuCalendar,
  LuCalendarClock,
  LuCheck,
  LuChevronDown,
  LuClock,
  LuMail,
  LuPencil,
  LuPlay,
  LuRefreshCw,
  LuSend,
  LuSquare,
  LuX,
} from "react-icons/lu";

import { InfoTip } from "../../../settings/team/editUserParts";
import { EmailTemplate } from "../../../settings/templates/emailTemplates/emailTemplates.types";
import {
  EmailSequenceOptions,
  EmailSequenceTemplate,
  SequenceForm,
  SequenceSendMode,
  SequenceStepForm,
} from "./emailSequence.types";
import {
  applySequenceTemplate,
  describeStartConfirm,
  MAX_SEQUENCE_STEPS,
  newStep,
  NO_OWN_MAILBOX,
  senderOptions,
  todayIso,
  validateSequenceForm,
} from "./emailSequence.utils";
import { SequenceEmailEditor } from "./SequenceEmailEditor";

export type SequenceModalMode = "new" | "edit" | "draft" | "template";

export type SequenceUserOption = { id: string; name: string; color?: string };

type Props = {
  /** Edit mode: the address the sequence is sent from (may be a colleague's; it keeps running). */
  currentFromEmail?: string | null;
  mode: SequenceModalMode;
  title?: string;
  initialForm: SequenceForm;
  options?: EmailSequenceOptions;
  isLoadingOptions?: boolean;
  emailTemplates: EmailTemplate[];
  sequenceTemplates?: EmailSequenceTemplate[];
  users?: SequenceUserOption[];
  /** Shown instead of the email choice when several contacts are selected (New work). */
  recipients?: string[];
  workName?: string;
  isSaving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (form: SequenceForm) => void;
  onStop?: () => void;
};

const TITLES: Record<SequenceModalMode, string> = {
  new: "New Sequence",
  draft: "New Sequence",
  edit: "Edit Sequence",
  template: "New sequence template",
};

const FIELD =
  "h-[42px] w-full rounded-lg border border-[#DCE2EA] bg-white px-3.5 text-[14px] text-[#1F2433] outline-none transition-colors focus:border-[#6B4EFF]";
const LABEL = "mb-2 block text-[14.5px] font-bold text-[#1F2433]";

const SelectBox: FC<{
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}> = ({ value, onChange, ariaLabel, disabled, children, className }) => (
  <div className={classNames("relative", className)}>
    <select
      aria-label={ariaLabel}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className={classNames(
        FIELD,
        "cursor-pointer appearance-none pr-9",
        disabled && "cursor-not-allowed !bg-[#F2F3F6] !text-[#8A90A2]",
      )}>
      {children}
    </select>
    <LuChevronDown
      size={15}
      className={
        "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7185]"
      }
    />
  </div>
);

const ModeButton: FC<{
  active: boolean;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  wide?: boolean;
  onClick: () => void;
}> = ({ active, disabled, icon, label, wide, onClick }) => (
  <button
    type={"button"}
    disabled={disabled}
    aria-pressed={active}
    onClick={onClick}
    className={classNames(
      "inline-flex h-9 items-center justify-center gap-2 rounded-lg border px-3.5 text-[13px] font-medium transition-colors",
      wide && "w-[330px] max-w-full",
      active
        ? "border-[#6B4EFF] bg-[#F0EDFF] text-[#5B3FE6]"
        : "border-[#DCE2EA] bg-white text-[#3A4256] hover:border-[#B9AEFF]",
      disabled &&
        "cursor-not-allowed !border-[#ECEEF2] !bg-[#F6F7F9] !text-[#A3A9B8]",
    )}>
    {icon}
    {label}
  </button>
);

const DaysInput: FC<{
  value: number;
  ariaLabel: string;
  min?: number;
  onChange: (value: number) => void;
}> = ({ value, ariaLabel, min = 1, onChange }) => (
  <input
    type={"number"}
    aria-label={ariaLabel}
    min={min}
    max={365}
    value={Number.isFinite(value) ? value : ""}
    onChange={(event) =>
      onChange(event.target.value === "" ? NaN : Number(event.target.value))
    }
    className={
      "h-9 w-[52px] rounded-lg border border-[#DCE2EA] text-center text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
    }
  />
);

/** Notify: several team members, shown as chips. */
const UsersPicker: FC<{
  users: SequenceUserOption[];
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ users, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);
  const selected = users.filter((user) => value.includes(user.id));
  const toggle = (id: string) =>
    onChange(
      value.includes(id) ? value.filter((item) => item !== id) : [...value, id],
    );

  return (
    <div ref={ref} className={"relative"}>
      <div
        role={"button"}
        tabIndex={0}
        aria-label={"Notify"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen((open) => !open);
          }
        }}
        className={classNames(
          "relative flex min-h-[42px] cursor-pointer flex-wrap items-center gap-1.5 rounded-lg border bg-white py-1.5 pl-3 pr-9",
          isOpen ? "border-[#6B4EFF]" : "border-[#DCE2EA]",
        )}>
        {selected.length ? (
          selected.map((user) => (
            <span
              key={user.id}
              className={
                "inline-flex items-center gap-1.5 rounded-md bg-[#1F2433] py-1 pl-2 pr-1.5 text-[12px] font-semibold text-white"
              }>
              {user.name}
              <button
                type={"button"}
                aria-label={`Remove ${user.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  toggle(user.id);
                }}
                className={"inline-flex text-white/70 hover:text-white"}>
                <LuX size={12} />
              </button>
            </span>
          ))
        ) : (
          <span className={"text-[14px] text-[#9AA0B0]"}>No one</span>
        )}
        <LuChevronDown
          size={15}
          className={"absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7185]"}
        />
      </div>
      {isOpen && (
        <div
          className={
            "absolute bottom-[calc(100%+6px)] left-0 right-0 z-20 max-h-[220px] overflow-y-auto rounded-lg border border-[#E8E8EC] bg-white p-1.5 shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
          }>
          {users.map((user) => {
            const checked = value.includes(user.id);
            return (
              <button
                key={user.id}
                type={"button"}
                role={"menuitemcheckbox"}
                aria-checked={checked}
                onClick={() => toggle(user.id)}
                className={
                  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13.5px] text-[#1F2433] hover:bg-[#F4F2FF]"
                }>
                <span
                  className={classNames(
                    "flex h-[18px] w-[18px] items-center justify-center rounded border-2",
                    checked
                      ? "border-[#6B4EFF] bg-[#6B4EFF] text-white"
                      : "border-[#C9CFD9]",
                  )}>
                  {checked && <LuCheck size={12} strokeWidth={3} />}
                </span>
                {user.name}
              </button>
            );
          })}
          {!users.length && (
            <div className={"px-2.5 py-2 text-[13px] text-[#8A90A2]"}>
              No team members found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SequenceModal: FC<Props> = ({
  mode,
  title,
  initialForm,
  options,
  isLoadingOptions,
  emailTemplates,
  sequenceTemplates = [],
  users = [],
  recipients,
  workName,
  isSaving,
  error,
  onClose,
  onSubmit,
  onStop,
  currentFromEmail,
}) => {
  const [form, setForm] = useState<SequenceForm>(initialForm);
  const [templateChoice, setTemplateChoice] = useState("");
  const [editing, setEditing] = useState<SequenceStepForm | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [customEmail, setCustomEmail] = useState(false);

  const isTemplate = mode === "template";
  const today = todayIso();
  // Only the person's own mailbox is offered (QA SEQ-013, owner 2026-10-22). Editing a running
  // sequence keeps the mailbox it was started from, even if it is a colleague's.
  const ownMailboxes = options?.mailboxes || [];
  const mailboxes = senderOptions(
    ownMailboxes,
    mode === "edit" ? initialForm.fromUserId : "",
    currentFromEmail,
  );
  const fromMailbox = mailboxes.find((box) => box.userId === form.fromUserId);
  const canUseYearEnd = !!options?.isOrganization && !!options?.yearEnd;
  const activeTemplates = useMemo(
    () => emailTemplates.filter((template) => template.isActive !== false),
    [emailTemplates],
  );

  // Pick the defaults once the work's options arrive.
  useEffect(() => {
    if (!options || isTemplate) return;
    setForm((current) => ({
      ...current,
      fromUserId:
        current.fromUserId &&
        (options.mailboxes.some((box) => box.userId === current.fromUserId) ||
          (mode === "edit" && current.fromUserId === initialForm.fromUserId))
          ? current.fromUserId
          : options.mailboxes[0]?.userId || "",
      toEmail: current.toEmail || options.contactEmails[0]?.email || "",
    }));
  }, [options, isTemplate, mode, initialForm.fromUserId]);

  const update = (patch: Partial<SequenceForm>) => {
    setProblem(null);
    setForm((current) => ({ ...current, ...patch }));
  };
  const updateStep = (key: string, patch: Partial<SequenceStepForm>) => {
    setProblem(null);
    setForm((current) => ({
      ...current,
      steps: current.steps.map((step) =>
        step.key === key ? { ...step, ...patch } : step,
      ),
    }));
  };

  const chooseTemplate = (step: SequenceStepForm, templateId: string) => {
    const template = activeTemplates.find((item) => item.id === templateId);
    updateStep(step.key, {
      emailTemplateId: templateId,
      subject: template?.subject || "",
      body: template?.body || "",
    });
  };

  const applyTemplate = (id: string) => {
    setTemplateChoice(id);
    const template = sequenceTemplates.find((item) => item.id === id);
    if (!template) return;
    setForm((current) => ({
      ...applySequenceTemplate(current, template, options?.statuses || []),
      name: workName ? `${template.name} – ${workName}` : template.name,
    }));
  };

  const handleSubmit = () => {
    const message = validateSequenceForm(form, {
      mode: isTemplate ? "template" : mode === "draft" ? "draft" : "sequence",
      canUseYearEnd,
      today,
    });
    if (message) {
      setProblem(message);
      return;
    }
    if (mode === "new") setIsConfirmOpen(true);
    else onSubmit(form);
  };

  const templateName = (step: SequenceStepForm) =>
    activeTemplates.find((item) => item.id === step.emailTemplateId)?.name ||
    step.subject ||
    "Email";

  const stopOption = (
    value: "mine" | "all",
    heading: string,
    detail: ReactNode,
    info: string,
  ) => {
    const active = form.stopScope === value;
    return (
      <label
        className={classNames(
          "block cursor-pointer rounded-[10px] border bg-white px-3.5 py-3 transition-colors",
          active
            ? "border-[#6B4EFF] shadow-[0_0_0_1px_#6B4EFF]"
            : "border-[#E4E7EC]",
        )}>
        <span className={"flex items-center gap-3"}>
          <input
            type={"radio"}
            name={"stopScope"}
            checked={active}
            onChange={() => update({ stopScope: value })}
            className={"h-4 w-4 accent-[#6B4EFF]"}
          />
          <span className={"flex-1 text-[13.5px] text-[#1F2433]"}>
            <span className={"font-semibold"}>{heading}</span> {detail}
          </span>
          <InfoTip text={info} />
        </span>
      </label>
    );
  };

  // Rendered on <body> so it also works inside other modals (New work).
  return createPortal(
    <div
      className={
        "fixed inset-0 z-[70] flex items-center justify-center bg-[rgba(20,22,34,0.45)] p-4 font-Manrope"
      }>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={title || TITLES[mode]}
        className={
          "flex max-h-[calc(100vh-32px)] w-[520px] max-w-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div
          className={
            "relative flex items-center justify-center border-b border-[#EDEFF3] px-6 py-5"
          }>
          <h3
            className={
              "m-0 flex items-center gap-2 text-[18px] font-extrabold text-[#1F2433]"
            }>
            {title || TITLES[mode]}
            <InfoTip
              tone={"purple"}
              placement={"bottom"}
              text={
                "Send a series of emails to the client. The sequence stops by itself when the client replies."
              }
            />
          </h3>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "absolute right-5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-[#8A90A2] hover:bg-[#F2F3F6]"
            }>
            <LuX size={18} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-6 py-5"}>
          {isTemplate && (
            <div className={"mb-5"}>
              <label className={LABEL} htmlFor={"sequence-name"}>
                Template Name <span className={"text-[#E5484D]"}>*</span>
              </label>
              <input
                id={"sequence-name"}
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                className={FIELD}
              />
            </div>
          )}

          {(mode === "new" || mode === "draft") && (
            <div
              className={
                "mb-5 rounded-xl border border-[#E6E0FF] bg-[#F8F6FF] p-4"
              }>
              <div
                className={
                  "mb-2.5 flex items-center gap-2 text-[14px] font-bold text-[#1F2433]"
                }>
                <LuMail size={16} className={"text-[#6B4EFF]"} />
                Start from a template
                <span className={"text-[12.5px] font-normal text-[#8A90A2]"}>
                  optional
                </span>
              </div>
              <SelectBox
                ariaLabel={"Start from a template"}
                value={templateChoice}
                onChange={applyTemplate}>
                <option value={""}>Start from scratch</option>
                {sequenceTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </SelectBox>
            </div>
          )}

          {!isTemplate && (
            <>
              <div
                className={
                  "mb-5 rounded-xl border border-l-[3px] border-[#E4E7EC] border-l-[#6B4EFF] p-4"
                }>
                <div
                  className={
                    "mb-2.5 flex items-center gap-2 text-[14px] font-bold text-[#1F2433]"
                  }>
                  <LuMail size={16} className={"text-[#6B4EFF]"} />
                  Send from
                  <InfoTip
                    tone={"purple"}
                    text={
                      "Sequences are sent from your own connected mailbox. Connect it on the Email page."
                    }
                  />
                </div>
                {mailboxes.length ? (
                  <SelectBox
                    ariaLabel={"Send from"}
                    value={form.fromUserId}
                    onChange={(value) => update({ fromUserId: value })}>
                    {mailboxes.map((box) => (
                      <option key={box.userId} value={box.userId}>
                        {box.email}
                      </option>
                    ))}
                  </SelectBox>
                ) : (
                  <div
                    className={
                      "rounded-lg bg-[#FFF7E6] px-3 py-2.5 text-[13px] text-[#8A5A00]"
                    }>
                    {isLoadingOptions ? "Loading mailboxes…" : NO_OWN_MAILBOX}
                  </div>
                )}
              </div>

              <div className={"mb-4"}>
                <span className={LABEL}>Contact</span>
                <div
                  className={classNames(
                    FIELD,
                    "flex items-center !bg-[#F2F3F6] !text-[#8A90A2]",
                  )}>
                  {recipients?.length
                    ? recipients.join(", ")
                    : options?.contactName || "—"}
                </div>
              </div>

              <div className={"mb-4"}>
                <label className={LABEL} htmlFor={"sequence-email"}>
                  Email Address{" "}
                  {mode !== "draft" && (
                    <span className={"text-[#E5484D]"}>*</span>
                  )}
                </label>
                {recipients && recipients.length > 1 ? (
                  <div
                    className={
                      "rounded-lg bg-[#F7F8FA] px-3.5 py-2.5 text-[13px] text-[#5B6678]"
                    }>
                    Each contact gets their own work item. Emails go to each
                    contact&apos;s primary email.
                  </div>
                ) : customEmail ? (
                  <div className={"flex gap-2"}>
                    <input
                      id={"sequence-email"}
                      type={"email"}
                      autoFocus
                      placeholder={"name@example.com"}
                      value={form.toEmail}
                      onChange={(event) =>
                        update({ toEmail: event.target.value })
                      }
                      className={FIELD}
                    />
                    <button
                      type={"button"}
                      onClick={() => {
                        setCustomEmail(false);
                        update({
                          toEmail: options?.contactEmails[0]?.email || "",
                        });
                      }}
                      className={
                        "shrink-0 rounded-lg border border-[#DCE2EA] px-3 text-[13px] text-[#3A4256]"
                      }>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <SelectBox
                    ariaLabel={"Email Address"}
                    value={form.toEmail}
                    onChange={(value) => {
                      if (value === "__new") {
                        setCustomEmail(true);
                        update({ toEmail: "" });
                      } else update({ toEmail: value });
                    }}>
                    {!options?.contactEmails.length && (
                      <option value={""}>
                        {mode === "draft"
                          ? "Contact's primary email"
                          : "No email on this contact"}
                      </option>
                    )}
                    {options?.contactEmails.map((item) => (
                      <option key={item.email} value={item.email}>
                        {item.email}
                        {item.isPrimary && options.contactEmails.length > 1
                          ? " (primary)"
                          : ""}
                      </option>
                    ))}
                    {form.toEmail &&
                      !options?.contactEmails.some(
                        (item) => item.email === form.toEmail,
                      ) && <option value={form.toEmail}>{form.toEmail}</option>}
                    <option value={"__new"}>+ Add new email…</option>
                  </SelectBox>
                )}
              </div>

              <div className={"mb-5"}>
                <label className={LABEL} htmlFor={"sequence-name"}>
                  Sequence Name <span className={"text-[#E5484D]"}>*</span>
                </label>
                <input
                  id={"sequence-name"}
                  value={form.name}
                  onChange={(event) => update({ name: event.target.value })}
                  className={FIELD}
                />
              </div>
            </>
          )}

          <div className={"border-t border-[#EDEFF3] pt-5"}>
            <div className={"mb-4 text-[14.5px] font-bold text-[#1F2433]"}>
              Emails
            </div>
            {form.steps.map((step, index) => {
              const isFirst = index === 0;
              const isSent = !!step.sentAt;
              const canRemove = !isSent && form.steps.length > 1;
              return (
                <div key={step.key}>
                  <div className={"flex items-center gap-2.5"}>
                    <span
                      className={
                        "flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#6B4EFF] text-[11.5px] font-bold text-white"
                      }>
                      {index + 1}
                    </span>
                    <span
                      className={"flex-1 text-[14px] font-bold text-[#1F2433]"}>
                      {isFirst ? "First Email" : "Follow-up Email"}
                    </span>
                    {isSent && (
                      <span
                        className={
                          "rounded-full bg-[#E7F7EE] px-2 py-0.5 text-[11px] font-bold text-[#178A4C]"
                        }>
                        SENT
                      </span>
                    )}
                    {canRemove && (
                      <button
                        type={"button"}
                        aria-label={`Remove email ${index + 1}`}
                        onClick={() =>
                          update({
                            steps: form.steps.filter(
                              (item) => item.key !== step.key,
                            ),
                          })
                        }
                        className={"text-[#A3A9B8] hover:text-[#E5484D]"}>
                        <LuX size={15} />
                      </button>
                    )}
                  </div>

                  <div
                    className={
                      "ml-[11px] border-l border-[#E4E7EC] pb-1 pl-[22px] pt-2"
                    }>
                    <div
                      className={
                        "mb-1.5 text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A90A2]"
                      }>
                      Template
                    </div>
                    {isSent ? (
                      <div className={"text-[13.5px] text-[#1F2433]"}>
                        {templateName(step)}
                        <div className={"mt-1 text-[12.5px] text-[#8A90A2]"}>
                          {isFirst
                            ? "Sent"
                            : `Sent ${step.delayDays} ${
                                step.delayDays === 1 ? "day" : "days"
                              } after the previous email`}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={"flex gap-2.5"}>
                          <SelectBox
                            className={"flex-1"}
                            ariaLabel={`Template for email ${index + 1}`}
                            value={step.emailTemplateId}
                            onChange={(value) => chooseTemplate(step, value)}>
                            {!step.emailTemplateId && (
                              <option value={""}>
                                {step.subject
                                  ? step.subject
                                  : "Pick a template…"}
                              </option>
                            )}
                            {activeTemplates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.name}
                              </option>
                            ))}
                          </SelectBox>
                          <button
                            type={"button"}
                            aria-label={`Edit email ${index + 1}`}
                            disabled={!step.subject && !step.body}
                            onClick={() => setEditing(step)}
                            className={
                              "flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg border border-[#DCE2EA] text-[#3A4256] hover:border-[#B9AEFF] disabled:opacity-40"
                            }>
                            <LuPencil size={15} />
                          </button>
                        </div>

                        {isFirst && !isTemplate && (
                          <div
                            className={
                              "mt-3 flex flex-col items-start gap-2.5"
                            }>
                            <ModeButton
                              wide
                              active={step.sendMode === "immediately"}
                              icon={<LuSend size={14} />}
                              label={"Send immediately"}
                              onClick={() =>
                                updateStep(step.key, {
                                  sendMode: "immediately",
                                })
                              }
                            />
                            <div className={"flex items-center gap-2.5"}>
                              <ModeButton
                                active={step.sendMode === "date"}
                                icon={<LuCalendar size={14} />}
                                label={"Schedule"}
                                onClick={() =>
                                  updateStep(step.key, {
                                    sendMode: "date" as SequenceSendMode,
                                    scheduledDate: step.scheduledDate || today,
                                  })
                                }
                              />
                              {step.sendMode === "date" && (
                                <input
                                  type={"date"}
                                  aria-label={"First email date"}
                                  min={today}
                                  value={step.scheduledDate}
                                  onChange={(event) =>
                                    updateStep(step.key, {
                                      scheduledDate: event.target.value,
                                    })
                                  }
                                  className={
                                    "h-9 rounded-lg border border-[#DCE2EA] px-2.5 text-[13px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
                                  }
                                />
                              )}
                            </div>
                            <div
                              className={"flex flex-wrap items-center gap-2.5"}>
                              <ModeButton
                                active={step.sendMode === "beforeYearEnd"}
                                disabled={!canUseYearEnd}
                                icon={<LuCalendarClock size={14} />}
                                label={"Before year end"}
                                onClick={() =>
                                  updateStep(step.key, {
                                    sendMode: "beforeYearEnd",
                                    delayDays: step.delayDays || 30,
                                  })
                                }
                              />
                              {step.sendMode === "beforeYearEnd" && (
                                <span
                                  className={
                                    "flex items-center gap-2 text-[13px] text-[#5B6678]"
                                  }>
                                  <DaysInput
                                    min={0}
                                    ariaLabel={"Days before year end"}
                                    value={step.delayDays}
                                    onChange={(value) =>
                                      updateStep(step.key, { delayDays: value })
                                    }
                                  />
                                  days before {options?.yearEnd}
                                </span>
                              )}
                            </div>
                            {!canUseYearEnd && (
                              <div className={"text-[12px] text-[#8A90A2]"}>
                                {options?.isOrganization
                                  ? "Add a year end to this organization to use this option."
                                  : "Only available for organizations — individuals have no year end."}
                              </div>
                            )}
                          </div>
                        )}

                        {isFirst && isTemplate && (
                          <div
                            className={
                              "mt-3 flex items-center gap-2 text-[13px] text-[#5B6678]"
                            }>
                            <LuClock size={15} className={"text-[#8A90A2]"} />
                            Send immediately, or after
                            <DaysInput
                              min={0}
                              ariaLabel={"Days before the first email"}
                              value={step.delayDays}
                              onChange={(value) =>
                                updateStep(step.key, { delayDays: value })
                              }
                            />
                            days
                          </div>
                        )}

                        {!isFirst && (
                          <div
                            className={
                              "mt-3 flex items-center gap-2 text-[13px] text-[#5B6678]"
                            }>
                            <LuClock size={15} className={"text-[#8A90A2]"} />
                            Send after
                            <DaysInput
                              ariaLabel={`Days before email ${index + 1}`}
                              value={step.delayDays}
                              onChange={(value) =>
                                updateStep(step.key, { delayDays: value })
                              }
                            />
                            days
                          </div>
                        )}
                      </>
                    )}
                    {index < form.steps.length - 1 && (
                      <div className={"mt-3 text-[12px] text-[#A3A9B8]"}>
                        then
                      </div>
                    )}
                  </div>
                  <div className={"h-3"} />
                </div>
              );
            })}

            <button
              type={"button"}
              disabled={form.steps.length >= MAX_SEQUENCE_STEPS}
              onClick={() =>
                update({ steps: [...form.steps, newStep(form.steps.length)] })
              }
              className={
                "mb-5 h-10 w-full rounded-lg border border-dashed border-[#B9AEFF] text-[13.5px] font-bold text-[#6B4EFF] hover:bg-[#F8F6FF] disabled:cursor-not-allowed disabled:opacity-50"
              }>
              + Add Email Step{" "}
              <span className={"text-[12px] font-normal text-[#8A90A2]"}>
                (Maximum {MAX_SEQUENCE_STEPS})
              </span>
            </button>
          </div>

          <div className={"mb-5 rounded-xl bg-[#F7F8FA] p-4"}>
            <div
              className={
                "mb-3 flex items-center gap-2 text-[14px] font-bold text-[#1F2433]"
              }>
              Stop the sequence when a reply arrives in:
              <InfoTip
                tone={"purple"}
                text={
                  "When the client replies to any email, the remaining emails are not sent."
                }
              />
            </div>
            <div className={"grid gap-2.5"}>
              {stopOption(
                "mine",
                "My email",
                <span className={"text-[#8A90A2]"}>
                  (
                  {isTemplate
                    ? "the sending mailbox"
                    : fromMailbox?.email || "the sending mailbox"}
                  )
                </span>,
                "Only replies that arrive in the mailbox this sequence is sent from.",
              )}
              {stopOption(
                "all",
                "All connected emails",
                null,
                "Replies that arrive in any mailbox connected by your team.",
              )}
            </div>
          </div>

          {!isTemplate && (
            <div className={"rounded-xl border border-[#E4E7EC] p-4"}>
              <div
                className={
                  "flex items-center gap-2 text-[14px] font-bold text-[#1F2433]"
                }>
                <LuRefreshCw size={15} className={"text-[#6B4EFF]"} />
                When the client replies
              </div>
              <p className={"mb-4 mt-1 text-[12.5px] text-[#8A90A2]"}>
                Automatically update this work item&apos;s status and notify
                your team once the client responds.
              </p>
              <label
                className={
                  "mb-1.5 block text-[13px] font-semibold text-[#3A4256]"
                }>
                Change the status to
              </label>
              <SelectBox
                ariaLabel={"Change the status to"}
                value={form.replyStatusId}
                onChange={(value) => update({ replyStatusId: value })}>
                <option value={""}>Don&apos;t change the status</option>
                {options?.statuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </SelectBox>
              <label
                className={
                  "mb-1.5 mt-4 block text-[13px] font-semibold text-[#3A4256]"
                }>
                Notify
              </label>
              <UsersPicker
                users={users}
                value={form.notifyUserIds}
                onChange={(value) => update({ notifyUserIds: value })}
              />
            </div>
          )}
        </div>

        {(problem || error) && (
          <div
            role={"alert"}
            className={
              "mx-6 mb-3 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
            }>
            {problem || error}
          </div>
        )}

        <div
          className={
            "flex items-center gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
          }>
          {mode === "edit" && onStop && (
            <button
              type={"button"}
              onClick={onStop}
              className={
                "inline-flex h-10 items-center gap-1.5 rounded-lg border border-[#F5C2C2] bg-[#FFF5F5] px-3.5 text-[13px] font-bold text-[#D92D20] hover:bg-[#FDECEC]"
              }>
              <LuSquare size={11} fill={"currentColor"} /> Stop sequence
            </button>
          )}
          <div className={"flex-1"} />
          <button
            type={"button"}
            onClick={onClose}
            className={
              "h-10 rounded-lg border border-[#DCE2EA] px-5 text-[14px] font-semibold text-[#1F2433] hover:bg-[#F7F8FA]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            disabled={isSaving || (!isTemplate && !mailboxes.length)}
            onClick={handleSubmit}
            className={
              "inline-flex h-10 items-center gap-2 rounded-lg bg-[#6B4EFF] px-5 text-[14px] font-bold text-white shadow-[0_6px_16px_rgba(107,78,255,0.3)] hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:opacity-50"
            }>
            {mode === "new" && <LuPlay size={12} fill={"currentColor"} />}
            {isSaving
              ? "Saving…"
              : mode === "new"
              ? "Start Sequence"
              : mode === "edit"
              ? "Save changes"
              : mode === "template"
              ? "Save template"
              : "Save sequence"}
          </button>
        </div>
      </div>

      {editing && (
        <SequenceEmailEditor
          title={templateName(editing)}
          subject={editing.subject}
          body={editing.body}
          onClose={() => setEditing(null)}
          onDone={(value) => {
            updateStep(editing.key, value);
            setEditing(null);
          }}
        />
      )}

      {isConfirmOpen && (
        <div
          className={
            "fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,22,34,0.45)] p-4"
          }>
          <div
            role={"alertdialog"}
            aria-label={"Start sequence"}
            className={
              "w-[400px] max-w-full rounded-2xl bg-white p-6 shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
            }>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-xl bg-[#6B4EFF] text-white"
              }>
              <LuMail size={18} />
            </span>
            <h4
              className={
                "mb-1.5 mt-4 text-[17px] font-extrabold text-[#1F2433]"
              }>
              Start &quot;{form.name}&quot;?
            </h4>
            <p className={"text-[13.5px] leading-[1.5] text-[#5B6678]"}>
              {describeStartConfirm(form, options?.yearEnd)}
            </p>
            <div className={"mt-5 grid grid-cols-2 gap-2.5"}>
              <button
                type={"button"}
                onClick={() => setIsConfirmOpen(false)}
                className={
                  "h-10 rounded-lg border border-[#DCE2EA] text-[14px] font-semibold text-[#1F2433]"
                }>
                Cancel
              </button>
              <button
                type={"button"}
                disabled={isSaving}
                onClick={() => {
                  setIsConfirmOpen(false);
                  onSubmit(form);
                }}
                className={
                  "h-10 rounded-lg bg-[#6B4EFF] text-[14px] font-bold text-white hover:bg-[#5A3EEB] disabled:opacity-50"
                }>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};
